import { debounce } from 'lodash'
import { makeAutoObservable, runInAction } from 'mobx'
import { isCancel } from 'axios'
import type { IDropdownItem } from 'shared/ui'
import { logger, truthy } from 'shared/lib'
import { RequestCancelAxios } from 'shared/api/lib'
import { Inbox } from 'entities/Inbox/model/Inbox'
import type { IParamsSendFrom } from 'entities/SendFrom'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { type IInboxCombine, InboxesApi } from 'entities/Inbox'
import { type IParamsTeamsSearch } from 'entities/Inbox/api/types'
import { type ISendFromStoreItem, type ISendFromStoreOption } from 'widgets/SendFrom'
import { getPFTInboxDropdownItemProps } from 'widgets/SendFrom/lib/getPFTInboxDropdownItemProps'
import { getDropdownItems, type IGetDropdownItemsOptions } from '../lib/getDropdownItems'

export type ISendFromStoreProps = {
  includeRCS?: boolean
  autoSelectInbox?: boolean
  excludeDeprioritizedNumbers?: boolean
  withDynamicSender?: boolean
  disabledPFT?: boolean
  personalInboxesOnly?: boolean
  getDropdownItemsOptions?: IGetDropdownItemsOptions
  onChangeInboxId?: (id?: number) => void
}

export class SendFromStore {
  initPromise
  private setFinishInit: ((value?: unknown) => void) | null = null
  private _config: ISendFromStoreProps | null = null
  private _loading = true
  private _loadingMore = false
  private _selectedDropdownItem: IDropdownItem<ISendFromStoreItem> | null = null
  private _topDropdownItems: IDropdownItem<ISendFromStoreItem>[] = []
  private _itemsMap: Map<number | string, IDropdownItem<ISendFromStoreItem>> = new Map()
  private _sendFromInit: IParamsSendFrom | null = null
  private _error = ''
  private _skipUnverifiedNumbers = false

  private _debounceLoad: ReturnType<typeof debounce>
  private _requestCancelAxios = new RequestCancelAxios()
  private _limit = 20
  private _page = 1
  private _total = 0
  private _search = ''

  constructor(private _entities: IEntitiesStore, props?: ISendFromStoreProps) {
    this._debounceLoad = debounce(this.load, 500)
    this.initPromise = new Promise((resolve) => {
      this.setFinishInit = resolve
    })
    this.setConfig(props)
    makeAutoObservable(this)
  }

  setConfig = (config?: ISendFromStoreProps) => {
    this._config = config || null
  }

  setLoading = (value: boolean) => {
    this._loading = value
  }

  init = async () => {
    await this.load()

    this._preselectItem()
  }

  load = async () => {
    const inboxes: Inbox[] = []

    try {
      this._requestCancelAxios.init()

      const response = await InboxesApi.getTeamsSearch(this.params, {
        cancelToken: this._requestCancelAxios.token,
      })
      const results = response.data.results
      const total = response.data.total

      results.forEach((item) => {
        this._entities.numbersStore.addItems(item.numbers)
        inboxes.push(new Inbox(item, this._entities))
      })

      runInAction(() => {
        this._total = total
      })

      this._addItems(inboxes)
      this.setLoading(false)
    } catch (e) {
      logger.error(e)

      if (!isCancel(e)) {
        this.setLoading(false)
      }
    }
  }

  loadMore = async () => {
    if (this._loadingMore) return
    if (!this.isMore) return

    try {
      runInAction(() => {
        this._loadingMore = true
      })

      const inboxes: Inbox[] = []

      this._requestCancelAxios.init()

      const response = await InboxesApi.getTeamsSearch(
        {
          ...this.params,
          page: this._page + 1,
        },
        {
          cancelToken: this._requestCancelAxios.token,
        }
      )
      const results = response.data.results
      const total = response.data.total

      results.forEach((item) => {
        this._entities.numbersStore.addItems(item.numbers)
        inboxes.push(new Inbox(item, this._entities))
      })

      runInAction(() => {
        this._page += 1
        this._total = total
        this._addItems(inboxes)
      })
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._loadingMore = false
      })
    }
  }

  onSearch = (value: string) => {
    this.resetPagination()
    this._requestCancelAxios.reset()
    this._debounceLoad?.cancel?.()
    this._itemsMap.clear()

    this._search = value

    this.setLoading(true)

    this._debounceLoad()
  }

  private _addItems = (inboxes: Inbox[]) => {
    const props = this._config

    if (!props?.includeRCS) inboxes = inboxes.filter((item) => !item.isRCS)

    const items: Array<IDropdownItem<ISendFromStoreItem> | false> = [
      !!props?.withDynamicSender && {
        id: 'from_contact_owner',
        iconL: 'user2',
        label: 'Contact owner',
        group: 'Dynamic sender',
      },
      ...getDropdownItems(inboxes, {
        getInboxDropdownItemProps: props?.disabledPFT
          ? (item) => {
              return getPFTInboxDropdownItemProps(item, this._entities)
            }
          : undefined,
        ...this._config?.getDropdownItemsOptions,
      }),
    ]

    this._setItemsDropdownItems(items.filter(truthy))
  }

  private _setItemsDropdownItems = (dropdownItems: IDropdownItem<ISendFromStoreItem>[]) => {
    dropdownItems.forEach((item) => {
      this._itemsMap.set(item.id, item)
    })
  }

  private _preselectItem = () => {
    const props = this._config

    if (!props?.autoSelectInbox) {
      this.setFinishInit?.()
      this.setLoading(false)

      return
    }

    const defaultOption = this._entities.inboxesStore.currentInbox
      ? {
          inbox_id: this._entities.inboxesStore.currentInbox?.id,
        }
      : undefined
    if (defaultOption) this.setSelectItem(defaultOption)
    this.setFinishInit?.()
    this.setLoading(false)
  }

  setSelectedDropdownItem = (selectedItem: IDropdownItem<ISendFromStoreItem> | null) => {
    if (!selectedItem) return
    if (this._selectedDropdownItem?.id === selectedItem.id) return

    const inboxIdSave = this.inbox_id

    this._selectedDropdownItem = selectedItem

    if (inboxIdSave !== selectedItem.data?.inbox?.id) {
      this._config?.onChangeInboxId?.(selectedItem.data?.inbox?.id)
    }
  }

  resetPagination = () => {
    this._search = ''
    this._total = 0
    this._limit = 20
    this._page = 1
    this._loadingMore = false
  }

  reset = () => {
    this.resetPagination()

    this._requestCancelAxios.reset()
    this._debounceLoad?.cancel?.()
    this._itemsMap.clear()
    this._selectedDropdownItem = null
  }

  setTopDropdownItems = (topDropdownItems: IDropdownItem<ISendFromStoreItem>[]) => {
    this._topDropdownItems = topDropdownItems
  }

  get search() {
    return this._search
  }

  get isMore() {
    return Math.ceil(this._total / this._limit) > this._page
  }

  get loadingMore() {
    return this._loadingMore
  }

  get config() {
    return this._config
  }

  get dropdownItems() {
    const items = Array.from(this._itemsMap.values())
      .map((item: IDropdownItem<ISendFromStoreItem>) => {
        if (!item.children) return item
        if (!this._skipUnverifiedNumbers) return item
        const filteredChildren: IDropdownItem<ISendFromStoreItem>[] = [
          ...item.children?.filter((child) => {
            const number = child.data?.number
            if (!number) return true
            if (number?.isTollFree) {
              return number?.isStatusVerified
            }
            return true
          }),
        ]
        if (!filteredChildren.length) return null
        return {
          ...item,
          children: filteredChildren,
        }
      })
      .filter(truthy)
    return [...this._topDropdownItems, ...items]
  }

  get params(): IParamsTeamsSearch {
    const params: IParamsTeamsSearch = {
      page: this._page,
      per_page: this._limit,
      exclude_deprioritized: this._config?.excludeDeprioritizedNumbers ? 1 : 0,
      by_all_members: !this._config?.personalInboxesOnly,
    }

    if (this.search) {
      params.term = this.search
    }

    return params
  }

  setSendFrom = async (params: IParamsSendFrom | null) => {
    if (!params) return
    this._sendFromInit = params

    const { type, options } = params

    if (type === 'from_number' || type === 'from_smart_inbox') {
      await this.setSelectItem({
        number_id: options.number_id,
        inbox_id: options.team_id,
        smart_option: options.smart_option,
      })
    }

    if (type === 'from_contact_owner') {
      await this.setSelectItem({
        id: 'from_contact_owner',
      })
    }

    if (type === 'from_integration') {
      await this.setSelectItem({
        id: 'from_integration',
        number_id: options.number_id,
        inbox_id: options.team_id,
        owner_property: options.owner_property,
      })
    }
  }

  private _fetchInbox = async (id: number, type: 'number' | 'inbox' = 'number') => {
    try {
      let inbox: IInboxCombine | undefined = undefined

      if (type === 'number') {
        inbox = await this._entities.inboxesStore.getInboxByNumberAsync(id)
      } else {
        inbox = await this._entities.inboxesStore.getById(id, true)
      }

      if (inbox instanceof Inbox) return inbox

      return null
    } catch (e) {
      logger.error(e)

      return null
    }
  }

  setSelectItem = async (
    { id, inbox_id, number_id, smart_option }: ISendFromStoreOption,
    isCashed?: boolean
  ) => {
    if (isCashed) {
      this.inbox_cashed_id = inbox_id || null
    }
    await this.initPromise

    let inbox: Inbox | null = null

    if (number_id) {
      inbox = await this._fetchInbox(number_id, 'number')
    }

    if (inbox_id) {
      inbox = await this._fetchInbox(inbox_id, 'inbox')
    }

    if (inbox) this._addItems([inbox])

    let item =
      this.dropdownItems.find((item) => item.id === id) ||
      (inbox_id && this._itemsMap.get(inbox_id)) ||
      null

    if (!item) {
      if (number_id) {
        item =
          this.dropdownItems.find((item) =>
            item.children?.find((child) => child.data.number?.id === number_id)
          ) || null
      }
    }
    if (!item) {
      runInAction(() => {
        this.inbox_cashed_id = null
      })
      return
    }

    const childId = smart_option || number_id
    if (childId) {
      const dropdownItem = !item.children
        ? null
        : item.children.reduce<IDropdownItem<ISendFromStoreItem> | null>((state, child) => {
            if (child.id === `${item?.id}_${childId}`) {
              return child
            }

            return state
          }, null)

      this.setSelectedDropdownItem(dropdownItem)
    } else {
      this.setSelectedDropdownItem(item.children?.[0] || item)
    }
    runInAction(() => {
      this.inbox_cashed_id = null
    })
  }

  setError = (value: string | boolean) => {
    if (typeof value === 'boolean') {
      this._error = value ? 'Please select the inbox to send from' : ''
    } else {
      this._error = value
    }
  }

  clearError = () => {
    this.setError('')
  }

  checkError = () => {
    this.setError(!this._selectedDropdownItem)

    return !!this._error
  }

  setSkipUnverifiedNumbers = (value: boolean) => {
    this._skipUnverifiedNumbers = value
  }

  get loading() {
    return this._loading
  }

  get error() {
    return this._error
  }

  private inbox_cashed_id: number | null = null
  get inbox() {
    if (this.inbox_cashed_id) {
      return this._entities.inboxesStore.getItem(this.inbox_cashed_id)
    }
    return this._selectedDropdownItem?.data?.inbox
  }

  get inbox_id() {
    return this.inbox?.id
  }

  get number() {
    return this._selectedDropdownItem?.data?.number
  }

  get number_id() {
    return this.number?.id
  }

  get smart_option() {
    return this._selectedDropdownItem?.data?.smart_option
  }

  get isAircall() {
    return !!this.number?.is_aircall
  }

  get selectedDropdownItem() {
    return this._selectedDropdownItem
  }

  get paramsSendFrom(): IParamsSendFrom | null {
    if (this._loading && this._sendFromInit) return this._sendFromInit

    if (this._selectedDropdownItem?.id === 'from_contact_owner') {
      return {
        type: 'from_contact_owner',
        options: {
          team_id: this.inbox_id,
          number_id: null,
        },
      }
    }

    if (this._selectedDropdownItem?.data?.integration) {
      return {
        type: 'from_integration',
        options: {
          owner_property: this._selectedDropdownItem.data?.mergeField?.name || '',
        },
      }
    }

    if (this.smart_option) {
      return {
        type: 'from_smart_inbox',
        options: {
          team_id: this.inbox_id,
          smart_option: this.smart_option,
        },
      }
    }

    if (this.inbox_id || this.number_id) {
      return {
        type: 'from_number',
        options: {
          team_id: this.inbox_id,
          number_id: this.number_id,
        },
      }
    }

    return null
  }
}
